import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const SeedUsers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const testUsers = [
    {
      id: 'admin',
      email: 'admin@socorreai.com',
      password: 'Teste@123',
      data: {
        name: 'Administrador',
        email: 'admin@socorreai.com',
        phone: '(11) 99999-0000',
        cpfCnpj: '000.000.000-00',
        photoURL: '',
        userType: 'admin',
        isAdmin: true,
        isTechnician: false,
        isSubscribed: false,
        createdAt: new Date().toISOString(),
      }
    },
    {
      id: 'technician',
      email: 'tecnico@socorreai.com',
      password: 'Teste@123',
      data: {
        name: 'João Técnico',
        email: 'tecnico@socorreai.com',
        phone: '(11) 98888-7777',
        cpfCnpj: '111.222.333-44',
        photoURL: '',
        userType: 'technician',
        isAdmin: false,
        isTechnician: true,
        isSubscribed: true,
        areas: ['Eletricista', 'Encanador'],
        city: 'São Paulo',
        description: 'Técnico experiente com mais de 10 anos de experiência em elétrica e hidráulica.',
        document: '111.222.333-44',
        isExempt: false,
        technicianStatus: 'approved',
        rating: 4.8,
        createdAt: new Date().toISOString(),
      }
    }
  ];

  const createTestUsers = async () => {
    setLoading(true);
    setResults([]);
    const newResults = [];

    for (const testUser of testUsers) {
      try {
        // Criar usuário no Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          testUser.email,
          testUser.password
        );

        const user = userCredential.user;

        // Adicionar dados no Firestore
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          ...testUser.data,
        });

        newResults.push({
          id: testUser.id,
          email: testUser.email,
          success: true,
          message: `✅ ${testUser.data.name} criado com sucesso!`,
          uid: user.uid,
        });

        toast.success(`${testUser.data.name} criado com sucesso!`);
      } catch (error) {
        console.error(`Error creating ${testUser.id}:`, error);
        
        let errorMessage = error.message;
        if (error.code === 'auth/email-already-in-use') {
          errorMessage = 'Email já está em uso. O usuário já existe!';
        } else if (error.code === 'auth/weak-password') {
          errorMessage = 'Senha muito fraca.';
        } else if (error.code === 'auth/invalid-email') {
          errorMessage = 'Email inválido.';
        }

        newResults.push({
          id: testUser.id,
          email: testUser.email,
          success: false,
          message: `❌ Erro: ${errorMessage}`,
        });

        toast.error(`Erro ao criar ${testUser.data.name}: ${errorMessage}`);
      }
    }

    setResults(newResults);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-2xl">🔧 Criar Usuários de Teste</CardTitle>
          <CardDescription>
            Esta página cria automaticamente os usuários de teste no Firebase
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Lista de usuários que serão criados */}
          <div className="space-y-4">
            <h3 className="font-bold">Usuários que serão criados:</h3>
            
            {testUsers.map((user) => (
              <div key={user.id} className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-bold">{user.data.name}</p>
                    <p className="text-sm text-slate-600">Email: {user.email}</p>
                    <p className="text-sm text-slate-600">Senha: {user.password}</p>
                    {user.data.isAdmin && (
                      <p className="text-sm text-blue-600 font-medium mt-1">👑 Administrador</p>
                    )}
                    {user.data.isTechnician && (
                      <p className="text-sm text-green-600 font-medium mt-1">
                        🔧 Técnico Aprovado (Assinatura Ativa)
                      </p>
                    )}
                  </div>
                  {results.find(r => r.id === user.id) && (
                    <div>
                      {results.find(r => r.id === user.id).success ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-600" />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Resultados */}
          {results.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-bold">Resultados:</h3>
              {results.map((result, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    result.success
                      ? 'bg-green-50 border-green-200 text-green-800'
                      : 'bg-red-50 border-red-200 text-red-800'
                  }`}
                >
                  <p className="font-medium">{result.message}</p>
                  {result.success && result.uid && (
                    <p className="text-xs mt-1">UID: {result.uid}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-4">
            <Button
              onClick={createTestUsers}
              disabled={loading}
              className="btn-primary flex-1"
              data-testid="create-users-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Criando usuários...
                </>
              ) : (
                'Criar Usuários de Teste'
              )}
            </Button>
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              disabled={loading}
            >
              Ir para Login
            </Button>
          </div>

          {/* Aviso */}
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>⚠️ Atenção:</strong> Se os usuários já existirem, você verá uma mensagem de erro.
              Isso é normal! Basta ir para a página de login e usar as credenciais.
            </p>
          </div>

          {results.length > 0 && results.every(r => r.success) && (
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <p className="text-sm text-green-800 font-medium mb-2">
                ✅ Todos os usuários foram criados com sucesso!
              </p>
              <p className="text-sm text-green-800">
                Você pode fazer login com:
              </p>
              <ul className="text-sm text-green-800 mt-2 space-y-1">
                <li>• Admin: admin@socorreai.com / Teste@123</li>
                <li>• Técnico: tecnico@socorreai.com / Teste@123</li>
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SeedUsers;
